import React, { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { parseDateKey } from '@/models/room-rate/rate-calendar'

type MemoPopoverProps = {
  dateKey: string
  memo: string
  position: { top: number; left: number }
  readOnly?: boolean
  onChangeMemo: (memo: string) => void
  onClose: () => void
}

export const MemoPopover: React.FC<MemoPopoverProps> = ({ dateKey, memo, position, readOnly = false, onChangeMemo, onClose }) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement | null>(null)
  const [value, setValue] = useState(memo)
  const valueRef = useRef(value)
  const originalRef = useRef(memo)
  valueRef.current = value

  const commitIfChanged = () => {
    // API 送信は trim 後と比較（空白だけの差分で不要な PUT を避ける）
    if (valueRef.current.trim() !== originalRef.current.trim()) {
      onChangeMemo(valueRef.current)
    }
  }

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (ref.current?.contains(event.target as Node)) {
        return
      }
      if (!readOnly) {
        commitIfChanged()
      }
      onClose()
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [onClose, readOnly])

  const { year, month, day } = parseDateKey(dateKey)
  const dateLabel = t('rateCalendar.dateLabel', { year, month, day })

  return (
    <div ref={ref} css={memoPopoverStyle} style={{ top: position.top, left: position.left }}>
      <div css={memoDateStyle}>{dateLabel}</div>
      {readOnly ? (
        <div css={memoReadOnlyStyle}>{memo}</div>
      ) : (
        <textarea
          css={memoInputStyle}
          value={value}
          autoFocus
          maxLength={500}
          rows={2}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              onClose()
            }
          }}
        />
      )}
    </div>
  )
}

const memoPopoverStyle = css({
  position: 'fixed',
  zIndex: 101,
  width: 280,
  background: '#fff',
  borderRadius: 10,
  boxShadow: '0px 3px 12px rgba(0,0,0,0.18)',
  padding: '16px 16px 20px',
})

const memoDateStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  color: '#272727',
  marginBottom: 12,
})

const memoReadOnlyStyle = css({
  minHeight: 58,
  fontSize: 14,
  lineHeight: '21px',
  color: '#272727',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
})

const memoInputStyle = css({
  width: '100%',
  height: 58,
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  borderRadius: 10,
  padding: '8px 12px',
  fontSize: 14,
  lineHeight: '21px',
  color: '#272727',
  outline: 'none',
  resize: 'none',
  fontFamily: 'inherit',
  '&:focus': {
    borderColor: '#F2A40B',
  },
})
